import { useCallback, useEffect, useState } from "react";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import PageBreadcrumb from "../../../shared/PageBreadCrumb";
import AuditTypeTable from "../components/AuditTypeTable";
import AuditTypeForm from "../components/AuditTypeForm";
import AuditTypeDetailView from "../components/AuditTypeDetailView";
import { AuditType, CreateAuditTypeData, auditTypeService } from "../../../services/auditTypeService";

const ITEMS_PER_PAGE = 6;

export default function AuditTypeMasters() {
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [totalAuditTypes, setTotalAuditTypes] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingAuditType, setEditingAuditType] = useState<AuditType | null>(null);
  const [viewingAuditType, setViewingAuditType] = useState<AuditType | null>(null);
  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: {
        page: number;
        limit: number;
        sortBy: string;
        search?: string;
        isActive?: boolean;
      } = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: `${sortField}:${sortOrder}`,
      };

      if (search) params.search = search;
      if (isActive !== null) params.isActive = isActive;

      const result = await auditTypeService.getAuditTypes(params);

      if ('meta' in result) {
        // Paginated response
        setAuditTypes(result.data);
        setTotalAuditTypes(result.meta?.totalDocs || 0);
      } else {
        // Single record or array response
        if (Array.isArray(result.data)) {
          setAuditTypes(result.data);
          setTotalAuditTypes(result.data.length);
        } else {
          setAuditTypes([result.data]);
          setTotalAuditTypes(1);
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch Audit Types", err);
      setError(err.message || "Failed to fetch Audit Types");
      setAuditTypes([]);
      setTotalAuditTypes(0);
    } finally {
      setLoading(false);
    }
  }, [search, isActive, page, sortField, sortOrder]);

  useEffect(() => {
    fetchAuditTypes();
  }, [fetchAuditTypes]);

  const handleAdd = async (auditTypeData: CreateAuditTypeData) => {
    try {
      if (editingAuditType && editingAuditType._id) {
        // Update existing Audit Type
        await auditTypeService.updateAuditType(editingAuditType._id, auditTypeData);
      } else {
        // Create new Audit Type
        await auditTypeService.createAuditType(auditTypeData);
      }
      setEditingAuditType(null);
      setOpenForm(false);
      // Refetch data from backend to ensure consistency
      fetchAuditTypes();
    } catch (error: any) {
      console.error("Failed to save Audit Type:", error);
      setError(error.message || "Failed to save Audit Type");
      throw error; // Re-throw to let AuditTypeForm handle the error display
    }
  };

  const handleView = (auditType: AuditType) => {
    setViewingAuditType(auditType);
    setShowDetailView(true);
  };

  const handleEdit = (auditType: AuditType) => {
    setEditingAuditType(auditType);
    setOpenForm(true);
  };

  const handleToggleStatus = async (auditType: AuditType) => {
    try {
      if (auditType._id) {
        if (auditType.isActive) {
          await auditTypeService.inactivateAuditType(auditType._id);
        } else {
          await auditTypeService.activateAuditType(auditType._id);
        }
        // Refresh to get updated data
        fetchAuditTypes();
      }
    } catch (error: any) {
      console.error("Failed to toggle Audit Type status:", error);
      setError(error.message || "Failed to toggle Audit Type status");
    }
  };

  return (
    <div className="max-w-9xl mx-auto space-y-6 relative">
      {/* Page Header */}
      <PageBreadcrumb pageTitle="Audit Types" />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Audit Type Table */}
      <AuditTypeTable
        auditTypes={auditTypes}
        search={search}
        setSearch={setSearch}
        isActive={isActive}
        setIsActive={setIsActive}
        page={page}
        setPage={setPage}
        totalAuditTypes={totalAuditTypes}
        onEdit={handleEdit}
        onView={handleView}
        onToggleStatus={handleToggleStatus}
        onAdd={() => {
          setEditingAuditType(null);
          setOpenForm(true);
        }}
        onRefresh={fetchAuditTypes}
        sortField={sortField}
        setSortField={setSortField}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        loading={loading}
      />

      {/* Audit Type Form Drawer */}
      <RightDrawer
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditingAuditType(null);
        }}
        size="60"
      >
        <div className="p-1">
          <AuditTypeForm
            editingAuditType={editingAuditType}
            onAdd={handleAdd}
            onClose={() => {
              setOpenForm(false);
              setEditingAuditType(null);
            }}
          />
        </div>
      </RightDrawer>

      {/* Audit Type Detail View Drawer */}
      <RightDrawer
        open={showDetailView}
        onClose={() => {
          setShowDetailView(false);
          setViewingAuditType(null);
        }}
        size="2xl"
      >
        <div className="h-full">
          <AuditTypeDetailView auditType={viewingAuditType} />
        </div>
      </RightDrawer>
    </div>
  );
}
